/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'DATE',
  branch: 'ALL',
  branch_receive: 'ALL',
  problem: 'ALL',
  status: 'ACTIVE',
  date_start: Func.dateTimeAdd(-30),
  date_stop: Func.dateTimeNow(),
};

const initSummary = {
  quantity: '',
  quantity_transfer: '',
  quantity_receive: '',
  quantity_balance: '',
};

function RpExportMove() {
  const mod = 'rp_export_move';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboStatus, setCboStatus] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');
  const [urlExcelReceive, setUrlExcelReceive] = useState('');
  const [urlExcelAll, setUrlExcelAll] = useState('');
  const [urlExcelExpress, setUrlExcelExpress] = useState('');
  const [buttonGroup, setButtonGroup] = useState(null);
  const [notify, setNotify] = useState(null);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.id', label: t(`attr.image`), align: 'center', width: 40 },
    { id: 'pd.code', label: 'สินค้า', align: 'left' },
    { id: 'es.code', label: 'เลขที่ส่ง', align: 'center' },
    { id: 'es.branch_receive', label: 'ส่ง / รับ', align: 'center' },
    { id: 'tb.problem_id', label: 'ปัญหา', align: 'center' },
    { id: 'tb.quantity', label: 'จำนวนขอ', align: 'right' },
    { id: 'tb.quantity_transfer', label: 'จำนวนให้', align: 'right' },
    { id: 'tb.quantity_receive', label: 'จำนวนรับ', align: 'right' },
    { id: 'tb.quantity_balance', label: 'ค้างรับ', align: 'right' },
    { id: 'tb.cost_fix', label: 'ราคาทุน', align: 'right' },
    { id: 'tb.lot', label: 'LOT', align: 'center' },
    { id: 'tb.date_approve', label: 'วันที่ให้', align: 'center' },
    { id: 'tb.date_receive', label: 'วันที่รับ', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      branch_receive: data.branch_receive,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      problem: data.problem,
      status: data.status,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const excelReceive = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch_receive=${find.branch_receive}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcelReceive}/${param}`, '_self');
    // window.open(`${urlExcelReceive}/${param}`, "_blank");
  };

  const excelAll = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&branch_receive=${find.branch_receive}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcelAll}/${param}`, '_self');
    // window.open(`${urlExcelReceive}/${param}`, "_blank");
  };

  const excelExpress = () => {
    setButtonGroup(null);

    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&branch_receive=${find.branch_receive}`;
    param += `&period=${find.period}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcelExpress}/${param}`, '_self');
    // window.open(`${urlExcelReceive}/${param}`, "_blank");
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboStatus(result.stepper);
        setUrlExcel(result.excel);
        setUrlExcelReceive(result.excel_receive);
        setUrlExcelAll(result.excel_all);
        setUrlExcelExpress(result.excel_express);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const expireDisplay = (item) => {
    if (item.is_expire === 'N') return '-';

    return Func.dateDisplay(item.date_expire);
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สาขาให้ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch_receive}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch_receive: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สาขารับ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.problem}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, problem: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>ปัญหา :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='Y'>เกิดปัญหา</MenuItem>
              <MenuItem value='N'>ไม่เกิดปัญหา</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='ACTIVE'>Active</MenuItem>
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={3}>
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton
                  variant='gradient'
                  color='success'
                  onClick={(e) => setButtonGroup(e.currentTarget)}
                >
                  <Icon>file_download</Icon>
                  &nbsp;Excel
                </MDButton>
                <Menu
                  sx={{ mt: '45px' }}
                  id='menu-appbar'
                  anchorEl={buttonGroup}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(buttonGroup)}
                  onClose={() => setButtonGroup(null)}
                >
                  <MenuItem onClick={excelAll}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;ให้และรับสินค้า
                    </MDTypography>
                  </MenuItem>
                  <MenuItem onClick={excel}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;ให้สินค้า
                    </MDTypography>
                  </MenuItem>
                  <MenuItem onClick={excelReceive}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;รับสินค้า
                    </MDTypography>
                  </MenuItem>
                  <MenuItem onClick={excelExpress}>
                    <MDTypography variant='body2'>
                      <i className='far fa-file-excel' />
                      &nbsp;Express
                    </MDTypography>
                  </MenuItem>
                </Menu>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const problemDisplay = (row) => {
    if (row.problem_id * 1 > 0 && row.problemr_id * 1 > 0) {
      return (
        <MDBox>
          <MDBox>
            <i className='fad fa-arrow-circle-right color-success' /> ให้ {row.problem_name}
          </MDBox>
          <MDBox>
            <i className='fad fa-arrow-circle-left color-danger' /> รับ {row.problemr_name}
          </MDBox>
        </MDBox>
      );
    }

    if (row.problem_id * 1 > 0) {
      return (
        <MDBox>
          <i className='fad fa-arrow-circle-right color-success' /> ให้ {row.problem_name}
        </MDBox>
      );
    }

    if (row.problemr_id * 1 > 0) {
      return (
        <MDBox>
          <i className='fad fa-arrow-circle-left color-danger' /> รับ {row.problemr_name}
        </MDBox>
      );
    }

    return null;
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_warehouse'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{Func.imageDisplay(row)}</TableCell>
                      <TableCell align='left'>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='cut' width={150}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.export_code}</MDBox>
                        <MDBox className='nowrap'>{Func.statusFaDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.branch_send} <Icon>arrow_right_alt</Icon> {row.branch_receive}
                      </TableCell>
                      <TableCell className='nowrap'>{problemDisplay(row)}</TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap color-info'>
                          {Func.numberDisplay(row.quantity)}
                        </MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap color-primary'>
                          {Func.numberDisplay(row.quantity_transfer)}
                        </MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap color-success'>
                          {Func.numberDisplay(row.quantity_receive)}
                        </MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='nowrap color-danger'>
                          {Func.numberDisplay(row.quantity_balance)}
                        </MDBox>
                        <MDBox className='nowrap'>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>
                        <MDBox className='nowrap'>{row.lot}</MDBox>
                        <MDBox className='nowrap'>{expireDisplay(row)}</MDBox>
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.date_approve)}
                      </TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.quantity_transfer === row.quantity_balance
                          ? '-'
                          : Func.dateTimeDisplay(row.date_receive)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={5}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='color-info'>
                      {Func.numberDisplay(summary.quantity)}
                    </TableCell>
                    <TableCell align='right' className='color-primary'>
                      {Func.numberDisplay(summary.quantity_transfer)}
                    </TableCell>
                    <TableCell align='right' className='color-success'>
                      {Func.numberDisplay(summary.quantity_receive)}
                    </TableCell>
                    <TableCell align='right' className='color-danger'>
                      {Func.numberDisplay(summary.quantity_balance)}
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_stock`), t(`menu.rp_export_move`)]} />
      {renderList()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

export default RpExportMove;
